import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getMe } from "../../lib/api";
import { liveApi, type AvailabilitySlot } from "../../lib/live-api";

function fmt(iso: string): string {
  return new Date(iso).toLocaleString("pl-PL", { dateStyle: "medium", timeStyle: "short" });
}

function localISO(d: Date): string {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function LiveAvailability(): JSX.Element {
  const [tutorId, setTutorId] = useState<string | null>(null);
  const [slots, setSlots] = useState<AvailabilitySlot[] | null>(null);
  const [start, setStart] = useState<string>(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 1);
    return localISO(d);
  });
  const [end, setEnd] = useState<string>(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 2);
    return localISO(d);
  });
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function load(): Promise<void> {
    setErr(null);
    try {
      const me = await getMe();
      if (!me.user) {
        setErr("Zaloguj się, aby zarządzać dostępnością.");
        return;
      }
      setTutorId(me.user.id);
      const from = new Date();
      const to = new Date(Date.now() + 30 * 24 * 3600 * 1000);
      const data = await liveApi.getAvailability(me.user.id, from, to);
      setSlots(data.slots);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  useEffect(() => { void load(); }, []);

  async function add(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      await liveApi.addAvailability(new Date(start), new Date(end), false);
      void load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string): Promise<void> {
    setBusy(true);
    try {
      await liveApi.deleteAvailability(id);
      void load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="p-8 max-w-3xl mx-auto text-ink-200">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-light tracking-tight">Moja dostępność (tutor)</h1>
        <Link to="/live" className="text-sm text-ink-400 hover:text-ink-200 underline">← Sesje live</Link>
      </div>

      {err && <div className="mb-4 px-3 py-2 rounded bg-red-900/40 border border-red-700 text-red-200 text-sm">{err}</div>}

      <div className="mb-8 border border-ink-700 rounded-xl p-4 bg-ink-800/30">
        <h2 className="text-sm uppercase tracking-widest text-ink-400 mb-3">Dodaj slot</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <label className="flex flex-col text-xs">
            <span className="text-ink-400 mb-1">Od</span>
            <input
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="bg-ink-800 border border-ink-700 rounded px-2 py-1.5"
            />
          </label>
          <label className="flex flex-col text-xs">
            <span className="text-ink-400 mb-1">Do</span>
            <input
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="bg-ink-800 border border-ink-700 rounded px-2 py-1.5"
            />
          </label>
          <button
            disabled={busy || !tutorId}
            onClick={() => void add()}
            className="px-4 py-2 rounded bg-lime-500/90 hover:bg-lime-400 text-ink-900 font-medium disabled:opacity-50"
          >
            Dodaj
          </button>
        </div>
      </div>

      <h2 className="text-sm uppercase tracking-widest text-ink-400 mb-3">Twoje sloty (najbliższe 30 dni)</h2>
      {slots === null ? (
        <div className="text-ink-500 text-sm">Ładowanie…</div>
      ) : slots.length === 0 ? (
        <div className="text-ink-500 text-sm">Brak slotów. Dodaj pierwszy powyżej.</div>
      ) : (
        <ul className="space-y-2">
          {slots.map((s) => (
            <li key={s.id} className="flex items-center justify-between border border-ink-700 rounded-lg px-3 py-2 bg-ink-800/30">
              <div className="text-sm">{fmt(s.starts_at)} – {fmt(s.ends_at)}</div>
              <button
                disabled={busy}
                onClick={() => void remove(s.id)}
                className="text-xs text-red-300 hover:text-red-200 underline disabled:opacity-50"
              >
                Usuń
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
